import React, { forwardRef } from 'react'
import { Button, IconButton } from '@material-ui/core'
import { alpha, useTheme } from '@material-ui/core/styles'

// One look for every result destination in the Analysis History "Result &
// Actions" column (also rendered by the cohort and carrier views through the
// GoTo* buttons). Only the main result is solid (`primary`); every other
// destination is a soft tonal button with no border, so a row of actions reads
// as one quiet strip with a single strong entry point. All are 30px high with
// the theme radius.
//   primary          solid fill; `tone` picks the colour: 'primary' (olive) for
//                    germline results, 'somatic' (violet) for somatic ones
//   variant="soft"   (default) light ink fill, text.primary label
//   variant="outlined" neutral outline, for an action that sits next to data
//                    rather than among the results (Run AI in Analysis Type)
// `compact` is the 26px size with fixed px type, so the button looks the same
// whatever the browser's font size.
export const RESULT_ACTION_HEIGHT = 30
export const RESULT_ACTION_COMPACT_HEIGHT = 26

// Soft fill: a light ink wash, one step stronger on hover. Dark surfaces need a
// little more ink for the same visible weight.
const softFill = (theme, hover = false) => {
  const isLight = theme.palette.mode === 'light'
  const base = isLight ? 0.06 : 0.08
  return alpha(theme.palette.text.primary, hover ? base + 0.04 : base)
}

const lookStyles = (theme, look, toneColors) => {
  if (look === 'solid') {
    return {
      color: toneColors.contrastText,
      backgroundColor: toneColors.main,
      '&:hover': {
        boxShadow: 'none',
        backgroundColor: toneColors.dark,
        // MUI resets contained hover to primary.main on touch screens.
        '@media (hover: none)': { backgroundColor: toneColors.main },
      },
    }
  }
  if (look === 'outlined') {
    return {
      color: theme.palette.text.primary,
      borderColor: alpha(theme.palette.grey[500], 0.4),
      '&:hover': {
        boxShadow: 'none',
        borderColor: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
      },
    }
  }
  return {
    color: theme.palette.text.primary,
    backgroundColor: softFill(theme),
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: softFill(theme, true),
      '@media (hover: none)': { backgroundColor: softFill(theme) },
    },
  }
}

const sizeStyles = (theme, compact) =>
  compact
    ? {
        height: RESULT_ACTION_COMPACT_HEIGHT,
        minHeight: RESULT_ACTION_COMPACT_HEIGHT,
        px: 1,
        fontSize: '13px',
        '& .MuiButton-startIcon': { marginRight: '4px', marginLeft: '-2px' },
        '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: '15px' },
      }
    : {
        height: RESULT_ACTION_HEIGHT,
        minHeight: RESULT_ACTION_HEIGHT,
        px: 1.25,
        fontSize: theme.typography.pxToRem(13),
        '& .MuiButton-startIcon': { marginRight: '4px', marginLeft: '-2px' },
        '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 16 },
      }

const MUI_VARIANT = { solid: 'contained', outlined: 'outlined', soft: 'text' }

const ResultButton = forwardRef(function ResultButton(
  { primary = false, variant = 'soft', tone = 'primary', compact = false, sx, children, ...other },
  ref,
) {
  const theme = useTheme()
  const toneColors = theme.palette[tone] || theme.palette.primary
  const look = primary ? 'solid' : MUI_VARIANT[variant] ? variant : 'soft'

  return (
    <Button
      ref={ref}
      size="small"
      variant={MUI_VARIANT[look]}
      color={look === 'solid' ? 'primary' : 'inherit'}
      sx={{
        minWidth: 0,
        borderRadius: `${theme.shape.borderRadius}px`,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        boxShadow: 'none',
        ...sizeStyles(theme, compact),
        ...lookStyles(theme, look, toneColors),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Button>
  )
})

// Icon-only actions (analysis details, pipeline tasks, hide): same height, always
// wrapped in a Tooltip by the caller.
export const ResultIconButton = forwardRef(function ResultIconButton({ sx, children, ...other }, ref) {
  const theme = useTheme()

  return (
    <IconButton
      ref={ref}
      size="small"
      sx={{
        width: RESULT_ACTION_HEIGHT,
        height: RESULT_ACTION_HEIGHT,
        flexShrink: 0,
        borderRadius: `${theme.shape.borderRadius}px`,
        color: theme.palette.text.secondary,
        '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
        '& svg': { fontSize: 18 },
        ...sx,
      }}
      {...other}
    >
      {children}
    </IconButton>
  )
})

export default ResultButton
